import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();

    minusButton.disabled = this.serverDisabledMinus || value <= min;

    // Keep the plus button interactive at the inventory limit so a customer
    // receives a useful inline explanation after trying to increase it.
    plusButton.disabled = this.serverDisabledPlus;
  }

  /**
   * Shows the existing cart item error only after the customer tries to exceed
   * the available quantity. No cart request is needed for a known local limit.
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax !== null && value >= effectiveMax) {
      event.preventDefault();
      this.#dispatchQuantityLimitError(effectiveMax);
      return;
    }

    super.increaseQuantity(event);
  }

  /**
   * Handles a manually entered quantity above the known inventory limit.
   * @param {Event} event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const requestedQuantity = Number.parseInt(event.target.value, 10);
    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax !== null && requestedQuantity > effectiveMax) {
      event.preventDefault();
      this.refs.quantityInput.value = this.refs.quantityInput.defaultValue;
      this.updateButtonStates();
      this.#dispatchQuantityLimitError(effectiveMax);
      return;
    }

    super.setQuantity(event);
  }

  /**
   * @param {number} maximum
   */
  #dispatchQuantityLimitError(maximum) {
    const { quantityInput } = this.refs;
    const messageTemplate = this.dataset.quantityErrorMax || '';
    const message = messageTemplate.replace('{{ maximum }}', maximum.toString());

    this.dispatchEvent(
      new CustomEvent('cart:quantity-limit', {
        bubbles: true,
        detail: {
          line: Number(quantityInput.dataset.cartLine),
          message,
        },
      }),
    );
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
